import { createAuthenticatedApi } from "@/lib/utils";
import type { GetEnrollmentResponse } from "@/openapi/generated";
import { ClassEnrollmentsApi } from "@/openapi/generated";

export const CLASS_ENROLLMENTS_PAGE_SIZE = 100;
const MAX_PAGES = 50;

/** `current` = active enrollments only; `all` = include scheduled future drops. */
export type ClassEnrollmentFetchScope = "current" | "all";

function toCursorId(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

const CLASS_ENROLLMENT_FETCH_CONCURRENCY = 8;

async function mapPool<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>,
): Promise<R[]> {
  if (items.length === 0) return [];
  const results: R[] = [];
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index]);
    }
  }

  const workerCount = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

export async function fetchEnrollmentsForClasses(
  accessToken: string,
  classIds: readonly number[],
  scope: ClassEnrollmentFetchScope = "all",
): Promise<GetEnrollmentResponse[]> {
  const lists = await mapPool(
    classIds,
    CLASS_ENROLLMENT_FETCH_CONCURRENCY,
    (classId) => fetchEnrollmentsForClass(accessToken, classId, scope),
  );
  return lists.flat();
}

/**
 * Loads every enrollment for a class via scroll pagination.
 * `page` stays 1; the API advances with `lastSelectedId`. Page++ without a
 * cursor overlaps rows on classes larger than the page size (e.g. 103 unique
 * students showing as 194 roster rows).
 */
export async function fetchEnrollmentsForClass(
  accessToken: string,
  classId: number,
  scope: ClassEnrollmentFetchScope = "all",
): Promise<GetEnrollmentResponse[]> {
  const api = createAuthenticatedApi(ClassEnrollmentsApi, accessToken);
  const out: GetEnrollmentResponse[] = [];
  const seenEnrollmentIds = new Set<number>();
  let lastSelectedId: number | undefined;
  let page = 1;

  while (page <= MAX_PAGES) {
    const res = await api.apiClassEnrollmentsClassesIdGet({
      id: classId,
      page: 1,
      pageSize: CLASS_ENROLLMENTS_PAGE_SIZE,
      lastSelectedId,
      active: scope === "current",
    });
    const items = res.items ?? [];
    if (items.length === 0) break;

    for (const item of items) {
      const enrollmentId = toCursorId(item.enrollmentId);
      if (enrollmentId != null) {
        if (seenEnrollmentIds.has(enrollmentId)) continue;
        seenEnrollmentIds.add(enrollmentId);
      }
      out.push(item);
    }

    if (res.hasNextPage === false) break;

    const cursor =
      toCursorId(res.lastSelectedId) ??
      toCursorId(items[items.length - 1]?.enrollmentId);
    if (cursor == null) break;

    lastSelectedId = cursor;
    page += 1;
  }

  return out;
}
